import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import { Upload, FileSpreadsheet, Plus, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const TestUpload = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('csv');
  const [uploading, setUploading] = useState(false);
  
  const [testName, setTestName] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  
  const [manualTestName, setManualTestName] = useState('');
  const [numQuestions, setNumQuestions] = useState(10);
  const [questions, setQuestions] = useState([]);

  // Subject-wise section and chapter mapping
  const subjectData = {
    Physics: {
      Mechanics: ['Newton\'s Laws', 'Work and Energy', 'Rotational Motion', 'Gravitation', 'Simple Harmonic Motion'],
      Thermodynamics: ['Laws of Thermodynamics', 'Kinetic Theory', 'Heat Transfer', 'Calorimetry'],
      Electromagnetism: ['Electrostatics', 'Current Electricity', 'Magnetism', 'Electromagnetic Induction', 'AC Circuits'],
      Optics: ['Ray Optics', 'Wave Optics', 'Optical Instruments'],
      'Modern Physics': ['Atomic Structure', 'Nuclear Physics', 'Photoelectric Effect', 'Dual Nature']
    },
    Chemistry: {
      'Physical Chemistry': ['Atomic Structure', 'Chemical Bonding', 'Thermodynamics', 'Equilibrium', 'Electrochemistry', 'Chemical Kinetics'],
      'Organic Chemistry': ['Hydrocarbons', 'Alcohols and Ethers', 'Aldehydes and Ketones', 'Carboxylic Acids', 'Amines', 'Biomolecules'],
      'Inorganic Chemistry': ['Periodic Table', 'Chemical Bonding', 'Coordination Compounds', 'Metallurgy', 'Group Elements']
    },
    Mathematics: {
      Algebra: ['Complex Numbers', 'Quadratic Equations', 'Sequences and Series', 'Permutations', 'Binomial Theorem', 'Matrices'],
      Calculus: ['Limits', 'Continuity', 'Differentiation', 'Integration', 'Differential Equations', 'Applications'],
      'Coordinate Geometry': ['Straight Lines', 'Circles', 'Parabola', 'Ellipse', 'Hyperbola', '3D Geometry'],
      Trigonometry: ['Ratios and Identities', 'Equations', 'Inverse Functions', 'Properties of Triangles'],
      Vectors: ['Vector Algebra', 'Scalar and Vector Products', '3D Vectors']
    },
    Biology: {
      Botany: ['Plant Physiology', 'Morphology', 'Reproduction', 'Genetics', 'Ecology', 'Plant Kingdom'],
      Zoology: ['Animal Physiology', 'Human Physiology', 'Reproduction', 'Genetics', 'Evolution', 'Animal Kingdom'],
      'Cell Biology': ['Cell Structure', 'Cell Division', 'Biomolecules', 'Enzymes'],
      Ecology: ['Ecosystem', 'Biodiversity', 'Environmental Issues']
    }
  };

  const subjects = Object.keys(subjectData);
  
  const mistakeTypes = [
    'Conceptual',
    'Calculation',
    'Misreading',
    'Casual',
    'Time Management'
  ];

  // Get sections for selected subject
  const getSections = (subject) => {
    return subject ? Object.keys(subjectData[subject] || {}) : [];
  };

  // Get chapters for selected subject and section
  const getChapters = (subject, section) => {
    return subject && section ? (subjectData[subject]?.[section] || []) : [];
  };

  const getTopics = (chapter) => {
    if (!chapter) return [];
    return [
      `${chapter} Fundamentals`,
      `${chapter} Key Formulas`,
      `${chapter} Problem Solving`
    ];
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvFile || !testName) {
      toast.error('Please provide test name and select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('test_name', testName);

    try {
      const response = await axios.post(`${API}/tests/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { test_name: testName }
      });
      toast.success(`Test uploaded! ${response.data.questions_added} questions added`);
      navigate(`/test/${response.data.test_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const initializeQuestions = () => {
    const newQuestions = [];
    for (let i = 0; i < numQuestions; i++) {
      newQuestions.push({
        question_number: i + 1,
        answer_status: 'Wrong',
        difficulty_level: 'Medium',
        difficulty_type: 'Mixed',
        time_spent: 60,
        mistake_type: null,
        subject: '',
        section: '',
        chapter: '',
        topic: ''
      });
    }
    setQuestions(newQuestions);
    toast.success(`${numQuestions} question fields added!`);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualTestName || questions.length === 0) {
      toast.error('Please provide test name and at least one question');
      return;
    }

    // Validate that all questions have required fields
    const invalidQuestions = questions.filter(q => !q.subject || !q.section || !q.chapter);
    if (invalidQuestions.length > 0) {
      toast.error('Please fill all required fields (Subject, Section, Chapter) for all questions');
      return;
    }

    // Validate that wrong answers have mistake type
    const wrongWithoutMistake = questions.filter(q => q.answer_status === 'Wrong' && !q.mistake_type);
    if (wrongWithoutMistake.length > 0) {
      toast.error('Please select mistake type for all wrong answers');
      return;
    }

    setUploading(true);
    try {
      const testResponse = await axios.post(`${API}/tests`, {
        test_name: manualTestName
      });
      const testId = testResponse.data.id;

      for (const question of questions) {
        await axios.post(`${API}/tests/${testId}/questions`, question);
      }

      toast.success(`Test created with ${questions.length} questions!`);
      navigate(`/test/${testId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create test');
    } finally {
      setUploading(false);
    }
  };

  const addQuestion = () => {
    setQuestions([...questions, {
      question_number: questions.length + 1,
      answer_status: 'Wrong',
      difficulty_level: 'Medium',
      difficulty_type: 'Mixed',
      time_spent: 0,
      mistake_type: null,
      subject: '',
      section: '',
      chapter: '',
      topic: ''
    }]);
  };

  const removeQuestion = (index) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    
    // Clear mistake_type when answer is Right
    if (field === 'answer_status' && value === 'Right') {
      updated[index].mistake_type = null;
    }
    
    // Reset section and chapter when subject changes
    if (field === 'subject') {
      updated[index].section = '';
      updated[index].chapter = '';
      updated[index].topic = '';
    }
    // Reset chapter when section changes
    if (field === 'section') {
      updated[index].chapter = '';
      updated[index].topic = '';
    }
    if (field === 'difficulty_level' && value !== 'Hard') {
      updated[index].difficulty_type = 'Mixed';
    }
    if (field === 'chapter') {
      updated[index].topic = '';
    }
    setQuestions(updated);
  };

  const downloadTemplate = () => {
    // Comprehensive CSV template with examples for all subjects
    const csvContent = `question_number,answer_status,difficulty_level,difficulty_type,time_spent,mistake_type,subject,section,chapter,topic
1,Wrong,Easy,Conceptual,120,Conceptual,Physics,Units & Measurement,SI Units,Base Units
2,Right,Medium,Calculation Heavy,90,,Physics,Kinematics,Projectile Motion,Range and Height
3,Wrong,Hard,Mixed,180,Calculation,Physics,Laws of Motion,Newton's Laws,Force Analysis
4,Right,Easy,Conceptual,60,,Physics,Electrostatics,Coulomb's Law,Electric Force
5,Wrong,Medium,Conceptual,150,Misreading,Physics,Optics,Reflection & Refraction,Mirror Formula
6,Right,Medium,Calculation Heavy,100,,Chemistry,Some Basic Concepts,Mole Concept,Molarity
7,Wrong,Hard,Conceptual,200,Conceptual,Chemistry,Atomic Structure,Quantum Numbers,Orbital Shapes
8,Right,Easy,Conceptual,80,,Chemistry,Chemical Bonding,Hybridization,sp3 Hybridization
9,Wrong,Medium,Calculation Heavy,150,Calculation,Chemistry,Equilibrium,pH,Buffer Solutions
10,Right,Hard,Mixed,180,,Chemistry,Biomolecules,Carbohydrates,Glucose Structure
11,Wrong,Easy,Conceptual,90,Casual,Mathematics,Complex Numbers,Argand Diagram,Complex Plane
12,Right,Medium,Calculation Heavy,120,,Mathematics,Differentiation,Chain Rule,Composite Functions
13,Wrong,Hard,Calculation Heavy,200,Calculation,Mathematics,Integral Calculus,Definite Integrals,Area Under Curve
14,Right,Easy,Conceptual,70,,Mathematics,Coordinate Geometry,Straight Lines,Slope Form
15,Wrong,Medium,Mixed,140,Time Management,Mathematics,Statistics & Probability,Bayes' Theorem,Conditional Probability
16,Right,Easy,Conceptual,80,,Biology,Cell Structure & Function,Cell Organelles,Mitochondria
17,Wrong,Medium,Conceptual,130,Conceptual,Biology,Genetics & Molecular Basis,DNA Replication,Enzymes Involved
18,Right,Hard,Mixed,170,,Biology,Human Physiology - Circulation,Cardiac Cycle,ECG Waves
19,Wrong,Easy,Conceptual,100,Misreading,Biology,Plant Physiology,Photosynthesis,Light Reactions
20,Right,Medium,Conceptual,110,,Biology,Evolution,Darwinism,Natural Selection`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wise_test_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded! Fill it with your test data and upload.');
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="test-upload-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">Analyze Offline Test</h1>
        <p className="text-lg text-slate-600">Add your offline test data for AI-powered analysis</p>
      </div>

      <Card className="border-0 shadow-xl">
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="csv" data-testid="csv-tab">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV Upload
              </TabsTrigger>
              <TabsTrigger value="manual" data-testid="manual-tab">
                <Plus className="w-4 h-4 mr-2" />
                Manual Entry
              </TabsTrigger>
            </TabsList>

            <TabsContent value="csv">
              <form onSubmit={handleCsvUpload} className="space-y-6" data-testid="csv-upload-form">
                <div className="space-y-2">
                  <Label htmlFor="test-name">Test Name</Label>
                  <Input
                    id="test-name"
                    placeholder="e.g., JEE Mock Test 1"
                    value={testName}
                    onChange={(e) => setTestName(e.target.value)}
                    required
                    data-testid="csv-test-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Upload CSV File</Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-violet-400 transition-colors duration-300">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      className="hidden"
                      id="csv-file-input"
                      data-testid="csv-file-input"
                    />
                    <label htmlFor="csv-file-input" className="cursor-pointer">
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-700 font-medium mb-1">
                          {csvFile ? csvFile.name : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-sm text-slate-500">CSV files only</p>
                      </motion.div>
                    </label>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-2">CSV Format Requirements:</h3>
                  <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                    <li>Headers: question_number, answer_status, difficulty_level, difficulty_type, time_spent, mistake_type, subject, section, chapter, topic</li>
                    <li>answer_status: &quot;Right&quot;, &quot;Wrong&quot;, or &quot;Not Attempted&quot;</li>
                    <li>difficulty_level: &quot;Easy&quot;, &quot;Medium&quot;, or &quot;Hard&quot;</li>
                    <li>difficulty_type: &quot;Conceptual&quot;, &quot;Calculation Heavy&quot;, &quot;Language Interpretation&quot;, or &quot;Mixed&quot;</li>
                    <li>time_spent: in seconds (numeric)</li>
                    <li>mistake_type: Required only if answer is &quot;Wrong&quot; (leave empty for &quot;Right&quot; answers)</li>
                    <li>subject: Physics, Chemistry, Mathematics, or Biology</li>
                  </ul>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadTemplate}
                    className="mt-3 bg-teal-500 hover:bg-teal-600 text-white border-teal-500 hover:border-teal-600"
                    data-testid="download-template-button"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11 font-medium shadow-lg shadow-violet-500/25"
                  disabled={uploading}
                  data-testid="csv-submit-button"
                >
                  {uploading ? 'Uploading...' : 'Upload and Analyze'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="manual">
              <form onSubmit={handleManualSubmit} className="space-y-6" data-testid="manual-entry-form">
                <div className="space-y-2">
                  <Label htmlFor="manual-test-name">Test Name</Label>
                  <Input
                    id="manual-test-name"
                    placeholder="e.g., NEET Practice Test"
                    value={manualTestName}
                    onChange={(e) => setManualTestName(e.target.value)}
                    required
                    data-testid="manual-test-name-input"
                  />
                </div>

                <div className="space-y-4 p-4 bg-violet-50 border border-violet-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="num-questions">How many questions do you want to add?</Label>
                      <Input
                        id="num-questions"
                        type="number"
                        min="1"
                        max="200"
                        value={numQuestions}
                        onChange={(e) => setNumQuestions(parseInt(e.target.value) || 10)}
                        className="w-32"
                        data-testid="num-questions-input"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={initializeQuestions}
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                      data-testid="initialize-questions-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Generate {numQuestions} Questions
                    </Button>
                  </div>
                  <p className="text-sm text-violet-700">
                    This will create {numQuestions} empty question forms for you to fill. You can change this number before generating.
                  </p>
                </div>

                {questions.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-lg font-semibold">Questions ({questions.length})</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={addQuestion}
                          variant="outline"
                          size="sm"
                          data-testid="add-question-button"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add One More
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                      {questions.map((question, index) => (
                        <Card key={`question-${question.question_number}-${index}`} className="border-slate-200" data-testid={`question-card-${index}`}>
                          <CardHeader className="pb-3">
                            <div className="flex justify-between items-center">
                              <CardTitle className="text-base">Question {index + 1}</CardTitle>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeQuestion(index)}
                                data-testid={`remove-question-button-${index}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Answer Status *</Label>
                                <Select
                                  value={question.answer_status}
                                  onValueChange={(value) => updateQuestion(index, 'answer_status', value)}
                                >
                                  <SelectTrigger data-testid={`answer-status-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Right">Right ✓</SelectItem>
                                    <SelectItem value="Wrong">Wrong ✗</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-1">
                                <Label className="text-xs">Difficulty *</Label>
                                <Select
                                  value={question.difficulty_level}
                                  onValueChange={(value) => updateQuestion(index, 'difficulty_level', value)}
                                >
                                  <SelectTrigger data-testid={`difficulty-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Easy">Easy</SelectItem>
                                    <SelectItem value="Medium">Medium</SelectItem>
                                    <SelectItem value="Hard">Hard</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-1">
                                <Label className="text-xs">Time (seconds) *</Label>
                                <Input
                                  type="number"
                                  value={question.time_spent}
                                  onChange={(e) => updateQuestion(index, 'time_spent', parseInt(e.target.value) || 0)}
                                  data-testid={`time-spent-${index}`}
                                  min="0"
                                />
                              </div>
                            </div>

                            {question.difficulty_level === 'Hard' && (
                              <div className="space-y-1">
                                <Label className="text-xs">Difficulty Type *</Label>
                                <Select
                                  value={question.difficulty_type}
                                  onValueChange={(value) => updateQuestion(index, 'difficulty_type', value)}
                                >
                                  <SelectTrigger data-testid={`difficulty-type-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Conceptual">Conceptual</SelectItem>
                                    <SelectItem value="Calculation Heavy">Calculation Heavy</SelectItem>
                                    <SelectItem value="Language Interpretation">Language Interpretation</SelectItem>
                                    <SelectItem value="Mixed">Mixed</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {question.answer_status === 'Wrong' && (
                              <div className="space-y-1">
                                <Label className="text-xs">Mistake Type *</Label>
                                <Select
                                  value={question.mistake_type || ''}
                                  onValueChange={(value) => updateQuestion(index, 'mistake_type', value)}
                                >
                                  <SelectTrigger data-testid={`mistake-type-${index}`}>
                                    <SelectValue placeholder="Select mistake type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {mistakeTypes.map((type) => (
                                      <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            <div className="space-y-1">
                              <Label className="text-xs">Subject *</Label>
                              <Select
                                value={question.subject}
                                onValueChange={(value) => updateQuestion(index, 'subject', value)}
                              >
                                <SelectTrigger data-testid={`subject-${index}`}>
                                  <SelectValue placeholder="Select subject" />
                                </SelectTrigger>
                                <SelectContent>
                                  {subjects.map((subject) => (
                                    <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Section *</Label>
                                <Select
                                  value={question.section}
                                  onValueChange={(value) => updateQuestion(index, 'section', value)}
                                  disabled={!question.subject}
                                >
                                  <SelectTrigger data-testid={`section-${index}`}>
                                    <SelectValue placeholder={question.subject ? "Select section" : "Select subject first"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getSections(question.subject).map((section) => (
                                      <SelectItem key={section} value={section}>{section}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Chapter *</Label>
                                <Select
                                  value={question.chapter}
                                  onValueChange={(value) => updateQuestion(index, 'chapter', value)}
                                  disabled={!question.section}
                                >
                                  <SelectTrigger data-testid={`chapter-${index}`}>
                                    <SelectValue placeholder={question.section ? "Select chapter" : "Select section first"} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getChapters(question.subject, question.section).map((chapter) => (
                                      <SelectItem key={chapter} value={chapter}>{chapter}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Topic *</Label>
                              <Select
                                value={question.topic}
                                onValueChange={(value) => updateQuestion(index, 'topic', value)}
                                disabled={!question.chapter}
                              >
                                <SelectTrigger data-testid={`topic-${index}`}>
                                  <SelectValue placeholder={question.chapter ? "Select topic" : "Select chapter first"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {getTopics(question.chapter).map((topic) => (
                                    <SelectItem key={topic} value={topic}>{topic}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {questions.length === 0 && (
                  <div className="text-center py-12 border-2 border-dashed border-slate-300 rounded-xl">
                    <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 mb-2">No questions added yet</p>
                    <p className="text-sm text-slate-500">Set the number of questions above and click &quot;Generate Questions&quot;</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11 font-medium shadow-lg shadow-violet-500/25"
                  disabled={uploading || questions.length === 0}
                  data-testid="manual-submit-button"
                >
                  {uploading ? 'Creating Test...' : `Create Test with ${questions.length} Questions`}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestUpload;